import { Badge, type BadgeVariant } from '@/shared/ui';
import tableStyles from '@/shared/ui/Table/Table.module.css';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { AuditFinding } from '../types';
import {
  findingAreaLabel,
  findingSearchTerm,
  groupFindings,
  impactLevel,
  type ImpactLevel,
} from '../utils/auditFindings';
import styles from './AuditFindings.module.css';

const IMPACT_BADGE: Record<ImpactLevel, { label: string; variant: BadgeVariant }> = {
  high: { label: 'High', variant: 'error' },
  medium: { label: 'Medium', variant: 'warning' },
  low: { label: 'Low', variant: 'neutral' },
};

function FindingTable({
  title,
  findings,
  group,
}: {
  title?: string;
  findings: AuditFinding[];
  group: string;
}) {
  if (findings.length === 0) return null;
  return (
    <div className={styles.group}>
      {title && <h3 className={styles.groupTitle}>{title}</h3>}
      {/* Bordered rows like the shared Table (same classes), without a header row. */}
      <div className={tableStyles.tableWrapper}>
        <table className={tableStyles.table}>
          <tbody>
            {findings.map((finding, index) => {
              const impact = IMPACT_BADGE[impactLevel(finding.impact)];
              const term = findingSearchTerm(finding);
              return (
                <tr key={`${group}:${index}`} className={tableStyles.tr}>
                  <td className={`${tableStyles.td} ${styles.number}`}>{index + 1}</td>
                  <td className={`${tableStyles.td} ${styles.areaCell}`}>
                    <span className={styles.area}>{findingAreaLabel(finding.kind)}</span>
                  </td>
                  <td className={tableStyles.td}>
                    <p className={styles.text}>{finding.finding}</p>
                    {term && <p className={styles.term}>Search term: “{term}”</p>}
                  </td>
                  <td className={`${tableStyles.td} ${styles.impactCell}`}>
                    <Badge size="sm" variant={impact.variant}>
                      {impact.label} impact
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * An audit's findings, laid out from the findings themselves (not the model's markdown): quick wins
 * first, then projects, each a numbered table ordered by impact. Every audit reads the same.
 */
export function AuditFindings({ findings }: { findings: AuditFinding[] }) {
  const { quickWins, projects, unsorted } = groupFindings(findings);
  return (
    <section className={styles.root} aria-label="Findings">
      <h2 className={patterns.sectionTitle}>Findings</h2>
      <FindingTable title="Quick wins: do this week" findings={quickWins} group="quick" />
      <FindingTable title="Projects: next 30 to 90 days" findings={projects} group="project" />
      <FindingTable findings={unsorted} group="other" />
    </section>
  );
}
